// assets/picopad-configurator.js
import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { findVariant, priceBreakdown, computeTotals } from '@theme/picopad-pricing';

const CARD_SELECTOR = '[data-picopad-card]';
const GROUP_SELECTOR = '[data-picopad-group]';
const VARIANT_GROUP_SELECTOR = '[data-picopad-variant-group]';
const SELECTED_CLASS = 'picopad-card--selected';

/**
 * @typedef {object} PicopadConfiguratorRefs
 * @property {HTMLElement} [formError] - Top-level error summary region.
 * @property {HTMLScriptElement} [variantData] - JSON list of {id, price, available, options}.
 * @property {HTMLElement} [summary] - "Your configuration" panel.
 * @property {HTMLElement} [summaryRows] - <dl> that lists the chosen options.
 * @property {HTMLElement} [summaryTotal] - Live total.
 * @property {HTMLElement} [summaryNote] - Availability / quantity note under the total.
 */

/**
 * @typedef {object} AddonState
 * @property {HTMLElement} group
 * @property {string} heading
 * @property {boolean} required
 * @property {string} value - Value stored in the line-item property ('' when nothing is selected).
 * @property {string} label - Human readable option name.
 * @property {number} priceMinor - Chargeable surcharge in minor units (0 when free / not chargeable).
 * @property {string} addonVariantId - Variant that carries the surcharge, '' when none.
 */

/**
 * Drives the PicoPad customizer.
 *
 * - Surface Finish / Keys are real product options rendered by
 *   snippets/picopad-variant-cards.liquid and driven by the theme's native
 *   <variant-picker>. This component only *reads* their state.
 * - Firmware / USB Cable (and any other Option Group block) are card groups
 *   stored as line-item properties. This component handles selection,
 *   keyboard navigation, validation and the hidden inputs.
 * - A live "Your configuration" summary shows the exact price the customer
 *   will pay: variant price + chargeable add-ons, times quantity. Every render
 *   recomputes from the DOM state, so nothing can accumulate or drift.
 * - Paid add-ons (cards with an add-on variant ID) are added to the cart as
 *   their own line right after the PicoPad is added, so checkout charges the
 *   same total the page displayed.
 *
 * @extends Component<PicopadConfiguratorRefs>
 */
class PicopadConfigurator extends Component {
  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {import('./picopad-pricing.js').VariantRecord[]} */
  #variants = [];

  /** Latest variant data confirmed by the theme's variant:update event. @type {{ id: number, price: number, available: boolean } | null} */
  #serverVariant = null;

  /** Hidden inputs moved into the product form, remembered so they can be put back. @type {Array<{ input: HTMLInputElement, group: HTMLElement | null }>} */
  #adoptedInputs = [];

  /** Paid add-ons captured when the form was submitted, added once the PicoPad itself is in the cart. @type {Array<{ addonVariantId: string, quantity: number, group: string, value: string, parentVariantId: string }>} */
  #pendingAddons = [];

  #onWindowSubmit = (/** @type {Event} */ event) => this.#handleSubmitCapture(event);
  #onChange = (/** @type {Event} */ event) => {
    if (event.target instanceof HTMLInputElement && event.target.classList.contains('picopad-card__input')) this.#render();
  };
  #onVariantUpdate = (/** @type {Event} */ event) => this.#handleVariantUpdate(event);
  #onCartUpdate = (/** @type {Event} */ event) => this.#handleCartUpdate(event);
  #onQuantityUpdate = () => this.#render();
  // The theme's quantity selector only announces on +/- and blur; this keeps the total live while typing.
  #onQuantityInput = (/** @type {Event} */ event) => {
    const { target } = event;
    if (target instanceof HTMLInputElement && target.name === 'quantity' && target.form === this.#productForm) this.#render();
  };

  connectedCallback() {
    super.connectedCallback();

    const form = this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null;
    this.#productForm = form instanceof HTMLFormElement ? form : null;
    if (!this.#productForm) {
      console.warn('[picopad-configurator] product form not found; validation and add-ons are disabled', this.dataset.productFormId);
    }
    this.#variants = this.#readVariants();

    this.#adoptHiddenInputs();
    this.#syncGroupsWithInputs();
    this.#guardExpressCheckout();

    // Capture on window so validation runs *before* the theme's document-level
    // on:submit handler (product-form-component) and can stop it.
    window.addEventListener('submit', this.#onWindowSubmit, true);
    this.addEventListener('change', this.#onChange);
    this.#sectionRoot().addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    document.addEventListener('input', this.#onQuantityInput);

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('submit', this.#onWindowSubmit, true);
    this.removeEventListener('change', this.#onChange);
    this.#sectionRoot().removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    document.removeEventListener('input', this.#onQuantityInput);

    // Hand the hidden inputs back to their groups so a theme-editor re-render
    // never leaves stale duplicates inside the product form.
    for (const { input, group } of this.#adoptedInputs) {
      if (group) group.append(input);
      else input.remove();
    }
    this.#adoptedInputs = [];
  }

  /* ------------------------------------------------------------------ */
  /* Declarative handlers (on:click / on:keydown on option cards)         */
  /* ------------------------------------------------------------------ */

  /**
   * Handles a click on a picopad-option-card (Firmware, USB Cable, ...).
   * The theme dispatches declarative handlers from a document-level listener,
   * so the card must be resolved from event.target, never currentTarget.
   * @param {Event} event
   */
  selectOptionCard(event) {
    const card = event.target instanceof Element ? event.target.closest(CARD_SELECTOR) : null;
    if (card instanceof HTMLElement) this.#selectCard(card);
  }

  /**
   * Arrow-key navigation inside a card group (WAI-ARIA radio group pattern).
   * @param {KeyboardEvent} event
   */
  handleCardKeydown(event) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;

    const card = event.target instanceof Element ? event.target.closest(CARD_SELECTOR) : null;
    const group = card?.closest(GROUP_SELECTOR);
    if (!(card instanceof HTMLElement) || !(group instanceof HTMLElement)) return;

    const cards = /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll(CARD_SELECTOR))).filter(
      (candidate) => candidate === card || !this.#isDisabled(candidate)
    );
    const next = cards[(cards.indexOf(card) + step + cards.length) % cards.length];
    if (!next || next === card) return;

    event.preventDefault();
    this.#selectCard(next);
    next.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Option-group state                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  #isDisabled(card) {
    return card.getAttribute('aria-disabled') === 'true';
  }

  /**
   * @param {HTMLElement} card
   */
  #selectCard(card) {
    const group = card.closest(GROUP_SELECTOR);
    if (!(group instanceof HTMLElement) || this.#isDisabled(card)) return;

    for (const sibling of group.querySelectorAll(CARD_SELECTOR)) {
      const selected = sibling === card;
      sibling.classList.toggle(SELECTED_CLASS, selected);
      sibling.setAttribute('aria-checked', String(selected));
    }

    const input = this.#groupInput(group);
    if (input) {
      input.value = card.dataset.value ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#setGroupError(group, false);
    this.#render();
  }

  /**
   * Moves each group's hidden property input inside the product form. The
   * form= attribute already associates them, but placing them inside the
   * form guarantees every serializer (FormData, native submit, dynamic
   * checkout buttons) picks them up. They are appended directly to the form,
   * outside `.product-form-buttons`, which the theme morphs on variant change.
   */
  #adoptHiddenInputs() {
    const form = this.#productForm;
    if (!form) return;

    for (const input of this.querySelectorAll('input[data-picopad-group-input]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const group = input.closest(GROUP_SELECTOR);
      form.append(input);
      this.#adoptedInputs.push({ input, group: group instanceof HTMLElement ? group : null });
    }
  }

  /**
   * Makes the visual state and the hidden input agree on page load and after
   * theme-editor re-renders. Exactly one enabled card per group ends up selected
   * (or none, when the group has no default).
   */
  #syncGroupsWithInputs() {
    for (const group of this.querySelectorAll(GROUP_SELECTOR)) {
      if (!(group instanceof HTMLElement)) continue;
      const input = this.#groupInput(group);
      const cards = /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll(CARD_SELECTOR)));
      const enabled = cards.filter((card) => !this.#isDisabled(card));
      let selected = enabled.find((card) => input && input.value !== '' && card.dataset.value === input.value);
      selected ??= enabled.find((card) => card.classList.contains(SELECTED_CLASS));

      for (const card of cards) {
        const on = card === selected;
        card.classList.toggle(SELECTED_CLASS, on);
        card.setAttribute('aria-checked', String(on));
      }
      if (input) input.value = selected?.dataset.value ?? '';
    }
  }

  /**
   * Express checkout buttons (Buy it now / wallets) skip the cart, so an
   * add-on line could not be attached and the customer would be undercharged.
   * They are hidden only when at least one *paid* add-on is configured; the
   * standard Add to cart flow carries everything correctly.
   *
   * Uses an inline style rather than the `hidden` attribute because the
   * theme's product form toggles `hidden` on this container on every variant
   * change; this is also re-applied after each variant:update.
   */
  #guardExpressCheckout() {
    if (!this.querySelector('[data-addon-variant-id]')) return;
    for (const container of this.#sectionRoot().querySelectorAll('.accelerated-checkout-block')) {
      if (container instanceof HTMLElement) {
        // Always re-apply: a morph can rewrite the style attribute.
        container.style.setProperty('display', 'none', 'important');
        container.dataset.picopadHidden = 'true';
      }
    }
  }

  /**
   * @param {HTMLElement} group
   * @returns {HTMLInputElement | null}
   */
  #groupInput(group) {
    const id = group.dataset.picopadGroup ?? '';
    const input = document.querySelector(`input[data-picopad-group-input="${CSS.escape(id)}"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {HTMLElement} group
   * @param {boolean} show
   */
  #setGroupError(group, show) {
    group.classList.toggle('picopad-card-group--error', show);
    const error = group.querySelector('.picopad-card-group__error');
    if (error instanceof HTMLElement) error.hidden = !show;
  }

  /* ------------------------------------------------------------------ */
  /* Reading the current configuration                                    */
  /* ------------------------------------------------------------------ */

  /** @returns {import('./picopad-pricing.js').VariantRecord[]} */
  #readVariants() {
    try {
      const parsed = JSON.parse(this.refs.variantData?.textContent ?? '[]');
      return Array.isArray(parsed)
        ? parsed.filter((v) => v && typeof v.id === 'number' && Number.isInteger(v.price) && Array.isArray(v.options))
        : [];
    } catch {
      return [];
    }
  }

  /**
   * Selected value of every real product option, in option-position order.
   * @returns {Array<{ heading: string, value: string | null }>}
   */
  #selectedOptions() {
    const fieldsets = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll(VARIANT_GROUP_SELECTOR))).sort(
      (a, b) => Number(a.dataset.fieldsetIndex) - Number(b.dataset.fieldsetIndex)
    );

    return fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return {
        heading: fieldset.dataset.heading || fieldset.dataset.optionName || '',
        value: checked instanceof HTMLInputElement ? checked.value : null,
      };
    });
  }

  /** @returns {AddonState[]} */
  #selectedAddons() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll(GROUP_SELECTOR))).map((group) => {
      const card = group.querySelector(`${CARD_SELECTOR}.${SELECTED_CLASS}`);
      const dataset = card instanceof HTMLElement ? card.dataset : {};
      const priceMinor = Number(dataset.priceMinor);
      return {
        group,
        heading: group.dataset.heading ?? '',
        required: group.dataset.required === 'true',
        value: dataset.value ?? '',
        label: dataset.label || dataset.value || '',
        priceMinor: Number.isInteger(priceMinor) && priceMinor > 0 ? priceMinor : 0,
        addonVariantId: dataset.addonVariantId ?? '',
      };
    });
  }

  /**
   * @param {Array<{ heading: string, value: string | null }>} options
   * @returns {import('./picopad-pricing.js').VariantRecord | null}
   */
  #resolveVariant(options) {
    let variant = null;

    if (options.length > 0) {
      variant = findVariant(
        this.#variants,
        options.map((option) => option.value)
      );
    } else {
      // Single-variant product: trust the form's variant id.
      const idField = this.#productForm?.elements.namedItem('id');
      const id = idField instanceof HTMLInputElement ? Number(idField.value) : NaN;
      variant = this.#variants.find((v) => v.id === id) ?? this.#variants[0] ?? null;
    }

    // Prefer the price/availability the theme just fetched for this variant.
    if (variant && this.#serverVariant && this.#serverVariant.id === variant.id) {
      variant = { ...variant, price: this.#serverVariant.price, available: this.#serverVariant.available };
    }

    return variant;
  }

  /** @returns {number} */
  #quantity() {
    const productFormComponent = this.#productForm?.closest('product-form-component');
    const fromSelector = Number(/** @type {any} */ (productFormComponent)?.refs?.quantitySelector?.getValue?.());
    if (Number.isFinite(fromSelector) && fromSelector > 0) return fromSelector;

    const field = this.#productForm?.elements.namedItem('quantity');
    const fromField = field instanceof HTMLInputElement ? Number(field.value) : NaN;
    return Number.isFinite(fromField) && fromField > 0 ? fromField : 1;
  }

  /**
   * @param {number} minorUnits
   * @returns {string}
   */
  #money(minorUnits) {
    try {
      return formatMoney(minorUnits, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'INR').replace(
        /<[^>]*>/g,
        ''
      );
    } catch {
      return (minorUnits / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Live summary                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Rebuilds the summary from scratch: base price, each option (with its
   * surcharge when the variant matrix is additive), each add-on, then the
   * total for the chosen quantity.
   */
  #render() {
    const options = this.#selectedOptions();
    const addons = this.#selectedAddons();
    const variant = this.#resolveVariant(options);
    const totals = computeTotals({ variant, addons, quantity: this.#quantity() });
    const breakdown = priceBreakdown(this.#variants, variant);
    const { summary, summaryRows, summaryTotal, summaryNote } = this.refs;

    if (summaryRows instanceof HTMLElement) {
      const fragment = document.createDocumentFragment();

      const basePrice = !variant ? '—' : this.#money(breakdown.additive ? breakdown.baseMinor : variant.price);
      fragment.append(this.#row(breakdown.additive || !variant ? 'Base price' : 'PicoPad price', basePrice, { className: 'picopad-summary__row--base' }));

      options.forEach((option, index) => {
        const delta = breakdown.additive ? breakdown.deltas[index] ?? 0 : 0;
        fragment.append(
          this.#row(option.heading, option.value ?? 'Select an option', {
            muted: !option.value,
            extra: delta > 0 ? `+${this.#money(delta)}` : '',
          })
        );
      });

      for (const addon of addons) {
        fragment.append(
          this.#row(addon.heading, addon.label || 'Select an option', {
            muted: !addon.label,
            extra: addon.priceMinor > 0 ? `+${this.#money(addon.priceMinor)}` : '',
          })
        );
      }

      summaryRows.replaceChildren(fragment);
    }

    if (summaryTotal instanceof HTMLElement) {
      summaryTotal.textContent = variant ? this.#money(totals.lineMinor) : '—';
    }

    if (summaryNote instanceof HTMLElement) {
      let note = '';
      if (!variant) {
        note = options.some((option) => !option.value)
          ? 'Select all options to see your price.'
          : 'This combination is not available.';
      } else if (!totals.available) {
        note = 'This combination is currently sold out.';
      } else if (totals.quantity > 1) {
        note = `${totals.quantity} × ${this.#money(totals.unitMinor)} each`;
      }
      summaryNote.textContent = note;
      summaryNote.hidden = note === '';
    }

    if (summary instanceof HTMLElement) {
      summary.classList.toggle('picopad-summary--unavailable', !variant || !totals.available);
    }
  }

  /**
   * @param {string} label
   * @param {string} value
   * @param {{ muted?: boolean, extra?: string, className?: string }} [options]
   * @returns {HTMLElement}
   */
  #row(label, value, { muted = false, extra = '', className = '' } = {}) {
    const row = document.createElement('div');
    row.className = `picopad-summary__row${className ? ` ${className}` : ''}`;

    const dt = document.createElement('dt');
    dt.textContent = label;

    const dd = document.createElement('dd');
    dd.textContent = value;
    if (muted) dd.classList.add('picopad-summary__value--muted');
    if (extra) {
      const span = document.createElement('span');
      span.className = 'picopad-summary__extra';
      span.textContent = extra;
      dd.append(' ', span);
    }

    row.append(dt, dd);
    return row;
  }

  /* ------------------------------------------------------------------ */
  /* Theme events                                                          */
  /* ------------------------------------------------------------------ */

  /** @returns {HTMLElement | Document} */
  #sectionRoot() {
    const root = this.closest('.shopify-section');
    return root instanceof HTMLElement ? root : document;
  }

  /**
   * @param {Event} event
   */
  #handleVariantUpdate(event) {
    const detail = /** @type {CustomEvent | { detail?: any }} */ (event).detail;
    const productId = detail?.data?.productId;
    if (productId != null && productId !== '' && String(productId) !== String(this.dataset.productId)) return;

    const resource = detail?.resource;
    if (resource && typeof resource.id === 'number') {
      this.#serverVariant = {
        id: resource.id,
        price: Number.isInteger(resource.price) ? resource.price : 0,
        available: Boolean(resource.available),
      };
    }

    // The theme's product form un-hides the express checkout container on every variant update.
    this.#guardExpressCheckout();
    this.#render();
  }

  /**
   * Validates required groups before the theme's product-form handler runs.
   * On success, remembers which paid add-ons must follow the PicoPad into the cart.
   * @param {Event} event
   */
  #handleSubmitCapture(event) {
    if (!this.#productForm || event.target !== this.#productForm) return;

    const addons = this.#selectedAddons();
    const invalid = addons.filter((addon) => addon.required && addon.value === '');
    for (const addon of addons) this.#setGroupError(addon.group, addon.required && addon.value === '');

    const { formError } = this.refs;

    if (invalid.length > 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#pendingAddons = [];

      const first = invalid[0];
      if (formError instanceof HTMLElement && first) {
        formError.hidden = false;
        formError.textContent = `${first.heading || 'This option'} is required before you can add this PicoPad to your cart.`;
      }
      first?.group.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      const firstCard = first?.group.querySelector(CARD_SELECTOR);
      if (firstCard instanceof HTMLElement) firstCard.focus();
      return;
    }

    if (formError instanceof HTMLElement) {
      formError.hidden = true;
      formError.textContent = '';
    }

    const variant = this.#resolveVariant(this.#selectedOptions());
    const quantity = this.#quantity();
    this.#pendingAddons = addons
      .filter((addon) => addon.addonVariantId !== '' && addon.priceMinor > 0)
      .map((addon) => ({
        addonVariantId: addon.addonVariantId,
        quantity,
        group: addon.heading,
        value: addon.label,
        parentVariantId: String(variant?.id ?? ''),
      }));
  }

  /**
   * Once the theme reports the PicoPad was added, add the paid add-on lines.
   * @param {Event} event
   */
  #handleCartUpdate(event) {
    const data = /** @type {CustomEvent | { detail?: any }} */ (event).detail?.data;
    if (!data || data.source !== 'product-form-component') return;
    if (data.productId != null && String(data.productId) !== String(this.dataset.productId)) return;

    const addons = this.#pendingAddons;
    this.#pendingAddons = [];
    if (data.didError || addons.length === 0) return;

    void this.#addAddonsToCart(addons);
  }

  /**
   * @param {Array<{ addonVariantId: string, quantity: number, group: string, value: string, parentVariantId: string }>} addons
   */
  async #addAddonsToCart(addons) {
    const routes = /** @type {any} */ (window).Theme?.routes ?? {};
    const addUrl = routes.cart_add_url ?? '/cart/add.js';
    const cartUrl = `${routes.cart_url ?? '/cart'}.js`;
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element.getAttribute('data-section-id'))
      .filter(Boolean);
    const productTitle = this.dataset.productTitle || 'PicoPad';

    const payload = {
      items: addons.map((addon) => ({
        id: Number(addon.addonVariantId),
        quantity: addon.quantity,
        properties: {
          'Add-on for': `${productTitle} (${addon.group}: ${addon.value})`,
          _picopad_parent_variant: addon.parentVariantId,
        },
      })),
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Add-on could not be added');
      }

      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null);

      this.dispatchEvent(
        new CartUpdateEvent(cart ?? {}, this.id || 'picopad-configurator', {
          source: 'picopad-configurator',
          itemCount: cart?.item_count,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );
    } catch (error) {
      const { formError } = this.refs;
      if (formError instanceof HTMLElement) {
        formError.hidden = false;
        formError.textContent = `Your PicoPad was added, but the add-on "${addons.map((a) => a.value).join(', ')}" could not be added. Please review your cart before checkout.`;
      }
      console.error('[picopad-configurator] add-on cart add failed', error);
    }
  }
}

if (!customElements.get('picopad-configurator-component')) {
  customElements.define('picopad-configurator-component', PicopadConfigurator);
}
